// Secure Team Service - Uses API endpoints instead of direct Supabase calls
// This prevents exposing database credentials and operations to the frontend

import { Team } from '../types';

export class SecureTeamService {
  private async getAuthToken(): Promise<string> {
    // Only use Supabase tokens for security - NO localStorage tokens
    try {
      const { supabase } = await import('../lib/supabase');
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session?.access_token && !error) {
          return session.access_token;
        }
      }
    } catch (error) {
      console.log('Supabase not available');
    }

    throw new Error('Authentication required - please sign in with Supabase');
  }

  /**
   * Save team using secure API endpoint
   */
  async saveTeam(team: Team): Promise<{ success: boolean; teamId?: string; fallback?: boolean }> {
    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/teams/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ team })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.fallback) {
          // Fallback to localStorage
          return this.saveTeamToLocalStorage(team);
        }
        throw new Error(data.error || 'Failed to save team');
      }

      return data;
    } catch (error) {
      console.error('Error saving team via API:', error);
      // Fallback to localStorage
      return this.saveTeamToLocalStorage(team);
    }
  }

  /**
   * Load teams using secure API endpoint
   */
  async loadTeams(): Promise<{ success: boolean; teams: any[]; fallback?: boolean }> {
    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/teams/load', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.fallback) {
          // Fallback to localStorage
          return this.loadTeamsFromLocalStorage();
        }
        throw new Error(data.error || 'Failed to load teams');
      }

      return data;
    } catch (error) {
      console.error('Error loading teams via API:', error);
      // Fallback to localStorage
      return this.loadTeamsFromLocalStorage();
    }
  }

  /**
   * Fallback: Save team to localStorage
   */
  private saveTeamToLocalStorage(team: Team): { success: boolean; fallback: boolean } {
    try {
      // Use the main team state storage format
      const currentState = localStorage.getItem('dream11-team-state');
      const state = currentState ? JSON.parse(currentState) : { 
        team: null, 
        notifications: [],
        selectedPosition: null,
        fieldOrientation: 'landscape',
        isLoading: false,
        saveError: null,
        hasUnsavedChanges: false,
        lastSaved: null
      };
      
      // Update the team
      state.team = team;
      state.hasUnsavedChanges = false; // Mark as saved
      state.lastSaved = new Date();
      
      localStorage.setItem('dream11-team-state', JSON.stringify(state));
      console.log('✅ Team saved to localStorage fallback');

      return { success: true, fallback: true };
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return { success: false, fallback: true };
    }
  }

  /**
   * Fallback: Load teams from localStorage
   */
  private loadTeamsFromLocalStorage(): { success: boolean; teams: any[]; fallback: boolean } {
    try {
      // Use the main team state storage format
      const savedState = localStorage.getItem('dream11-team-state');
      if (savedState) {
        const state = JSON.parse(savedState);
        if (state.team) {
          return { 
            success: true, 
            teams: [state.team], 
            fallback: true 
          };
        }
      }

      return { success: true, teams: [], fallback: true };
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return { success: true, teams: [], fallback: true };
    }
  }

  /**
   * Migrate team data from localStorage to Supabase
   */
  async migrateFromLocalStorage(userId: string): Promise<boolean> {
    try {
      const localTeamData = localStorage.getItem('dream11-team-state');
      if (!localTeamData) {
        return false;
      }

      // Check if migration already completed
      const migrationKey = `migration-completed-${userId}`;
      if (localStorage.getItem(migrationKey)) {
        return true;
      }

      const teamState = JSON.parse(localTeamData);
      const team = teamState.team;

      if (team && team.name) {
        const result = await this.saveTeam(team);
        if (result.success && !result.fallback) {
          localStorage.setItem(migrationKey, 'true');
          console.log('Successfully migrated team data to Supabase');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error migrating team from localStorage:', error);
      return false;
    }
  }
}

export const secureTeamService = new SecureTeamService(); 